/**
 * Records a Firmio Meet meeting from the host's browser and ships it to the
 * backend (→ Google Drive) while it runs.
 *
 * What gets recorded: the screen surface the host picks in the browser's
 * share dialog (the meeting tab, ideally — Chrome offers "this tab" first)
 * plus ONE mixed audio track: the host's microphone, every remote
 * participant's audio, and whatever audio the chosen surface carries. The
 * mix is done with the Web Audio API so mute state and late joiners are
 * handled: tracks subscribed after the start are added to the mix as they
 * arrive.
 *
 * Transport: MediaRecorder emits a WebM chunk every few seconds; each is
 * POSTed with a sequence number and retried a few times. Chunks are only
 * valid appended in order, so an upload that keeps failing stops the
 * recording rather than producing a corrupt file. Stopping (button, screen
 * share ended, leaving the meeting) flushes the last chunk and calls finish.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { RoomEvent, Track, type RemoteTrack, type Room } from 'livekit-client';
import { finishRecording, startRecording, uploadRecordingChunk } from '../lib/meetApi';

export type MeetRecorderStatus = 'idle' | 'starting' | 'recording' | 'stopping' | 'error';

export type MeetRecorderErrorKey =
  | 'meet.recording.error.denied'
  | 'meet.recording.error.unsupported'
  | 'meet.recording.error.upload'
  | 'meet.recording.error.generic';

const CHUNK_MS = 5000;
const UPLOAD_RETRIES = 4;

const MIME_CANDIDATES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4',
];

export function canRecordMeeting(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getDisplayMedia)
  );
}

function pickMime(): string | null {
  for (const m of MIME_CANDIDATES) if (MediaRecorder.isTypeSupported(m)) return m;
  return null;
}

export function useMeetRecorder(getRoom: () => Room | null, meetingId: string | null) {
  const [status, setStatus] = useState<MeetRecorderStatus>('idle');
  const [error, setError] = useState<MeetRecorderErrorKey | null>(null);
  const [seconds, setSeconds] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordingIdRef = useRef<string | null>(null);
  const displayRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const mixedTracksRef = useRef<Set<MediaStreamTrack>>(new Set());
  const seqRef = useRef(0);
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const failedRef = useRef(false);
  const startedAtRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);
  const roomListenerRef = useRef<{ room: Room; handler: (t: RemoteTrack) => void } | null>(null);
  const stoppingRef = useRef(false);

  const addToMix = useCallback((track: MediaStreamTrack) => {
    const ctx = ctxRef.current;
    const destination = (
      ctx as (AudioContext & { __dest?: MediaStreamAudioDestinationNode }) | null
    )?.__dest;
    if (!ctx || !destination || mixedTracksRef.current.has(track)) return;
    try {
      const source = ctx.createMediaStreamSource(new MediaStream([track]));
      source.connect(destination);
      mixedTracksRef.current.add(track);
      track.addEventListener('ended', () => mixedTracksRef.current.delete(track));
    } catch {
      // A track that cannot be mixed (already ended) is simply skipped.
    }
  }, []);

  const teardownMedia = useCallback(() => {
    if (tickRef.current != null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    const listener = roomListenerRef.current;
    if (listener) {
      listener.room.off(RoomEvent.TrackSubscribed, listener.handler);
      roomListenerRef.current = null;
    }
    displayRef.current?.getTracks().forEach((t) => t.stop());
    displayRef.current = null;
    mixedTracksRef.current.clear();
    void ctxRef.current?.close().catch(() => undefined);
    ctxRef.current = null;
    recorderRef.current = null;
  }, []);

  /** Upload chunk `seq`, retrying; a persistent failure marks the recording broken. */
  const enqueue = useCallback((recordingId: string, seq: number, blob: Blob) => {
    queueRef.current = queueRef.current.then(async () => {
      if (failedRef.current) return;
      for (let attempt = 1; attempt <= UPLOAD_RETRIES; attempt += 1) {
        try {
          await uploadRecordingChunk(recordingId, seq, blob);
          return;
        } catch (err) {
          const status = (err as { status?: number } | null)?.status;
          // 409 = the server already has it / recording closed: not retryable.
          if (status === 409 || status === 404) {
            failedRef.current = true;
            return;
          }
          await new Promise((r) => setTimeout(r, 700 * attempt));
        }
      }
      failedRef.current = true;
    });
  }, []);

  const stop = useCallback(async () => {
    if (stoppingRef.current) return;
    const recorder = recorderRef.current;
    const recordingId = recordingIdRef.current;
    if (!recorder || !recordingId) {
      teardownMedia();
      setStatus('idle');
      return;
    }
    stoppingRef.current = true;
    setStatus('stopping');
    await new Promise<void>((resolve) => {
      if (recorder.state === 'inactive') {
        resolve();
        return;
      }
      recorder.addEventListener('stop', () => resolve(), { once: true });
      try {
        recorder.stop();
      } catch {
        resolve();
      }
    });
    // Let the last dataavailable land in the queue, then drain it.
    await new Promise((r) => setTimeout(r, 50));
    await queueRef.current;
    const uploadFailed = failedRef.current;
    try {
      await finishRecording(meetingId ?? '', recordingId);
    } catch {
      // The sweep will finish it server-side.
    }
    teardownMedia();
    recordingIdRef.current = null;
    stoppingRef.current = false;
    if (uploadFailed) {
      setError('meet.recording.error.upload');
      setStatus('error');
    } else {
      setStatus('idle');
    }
  }, [meetingId, teardownMedia]);

  const start = useCallback(async () => {
    const room = getRoom();
    if (!meetingId || !room || status !== 'idle') return;
    if (!canRecordMeeting()) {
      setError('meet.recording.error.unsupported');
      setStatus('error');
      return;
    }
    setError(null);
    setStatus('starting');
    failedRef.current = false;
    seqRef.current = 0;
    queueRef.current = Promise.resolve();

    try {
      // Ask for the surface first: cancelling the dialog costs nothing server-side.
      const display = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 15, max: 30 } },
        audio: true,
        // Chrome: offer the current tab first, no need to hunt for it.
        ...({ preferCurrentTab: true, selfBrowserSurface: 'include' } as object),
      });
      displayRef.current = display;

      // Mixed audio: display audio (if any) + our mic + every remote participant.
      const ctx = new AudioContext();
      const destination = ctx.createMediaStreamDestination();
      (ctx as AudioContext & { __dest?: MediaStreamAudioDestinationNode }).__dest = destination;
      ctxRef.current = ctx;
      display.getAudioTracks().forEach(addToMix);
      const micPub = room.localParticipant.getTrackPublication(Track.Source.Microphone);
      const micTrack = micPub?.track?.mediaStreamTrack;
      if (micTrack) addToMix(micTrack);
      for (const participant of room.remoteParticipants.values()) {
        for (const pub of participant.audioTrackPublications.values()) {
          const t = pub.track?.mediaStreamTrack;
          if (t) addToMix(t);
        }
      }
      const onSubscribed = (track: RemoteTrack) => {
        if (track.kind === Track.Kind.Audio && track.mediaStreamTrack)
          addToMix(track.mediaStreamTrack);
      };
      room.on(RoomEvent.TrackSubscribed, onSubscribed);
      roomListenerRef.current = { room, handler: onSubscribed };

      const mime = pickMime();
      if (!mime) throw new Error('unsupported');
      const created = await startRecording(meetingId, mime.split(';')[0]);
      recordingIdRef.current = created.id;

      const stream = new MediaStream([
        ...display.getVideoTracks(),
        ...destination.stream.getAudioTracks(),
      ]);
      const recorder = new MediaRecorder(stream, {
        mimeType: mime,
        videoBitsPerSecond: 1_800_000,
        audioBitsPerSecond: 96_000,
      });
      recorderRef.current = recorder;
      recorder.addEventListener('dataavailable', (event) => {
        if (event.data && event.data.size > 0 && recordingIdRef.current) {
          const seq = seqRef.current;
          seqRef.current += 1;
          enqueue(recordingIdRef.current, seq, event.data);
        }
      });
      // The host ended the share from the browser's own bar: that is a stop.
      display.getVideoTracks()[0]?.addEventListener('ended', () => void stop());
      recorder.start(CHUNK_MS);

      startedAtRef.current = Date.now();
      setSeconds(0);
      tickRef.current = window.setInterval(() => {
        if (startedAtRef.current != null) {
          setSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
        }
        // An upload that gave up ends the recording instead of growing a corrupt file.
        if (failedRef.current) void stop();
      }, 1000);
      setStatus('recording');
    } catch (err) {
      teardownMedia();
      const name = err instanceof Error ? err.name : '';
      const message = err instanceof Error ? err.message : '';
      setError(
        name === 'NotAllowedError' || name === 'AbortError'
          ? 'meet.recording.error.denied'
          : message === 'unsupported' || name === 'NotSupportedError'
            ? 'meet.recording.error.unsupported'
            : 'meet.recording.error.generic',
      );
      setStatus(name === 'NotAllowedError' || name === 'AbortError' ? 'idle' : 'error');
    }
  }, [addToMix, enqueue, getRoom, meetingId, status, stop, teardownMedia]);

  const dismissError = useCallback(() => {
    setError(null);
    setStatus((s) => (s === 'error' ? 'idle' : s));
  }, []);

  // Unmount (navigating away): stop and finish so nothing lingers on disk.
  useEffect(() => {
    return () => {
      if (recorderRef.current) void stop();
    };
  }, []);

  return { status, error, seconds, start, stop, dismissError };
}
